import { useState, useEffect } from 'react';
import { FileText, Plus, Filter, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { InvoiceDetailModal } from './InvoiceDetailModal';
import { CreateStandaloneInvoiceModal } from './CreateStandaloneInvoiceModal';

interface Invoice {
  id: string;
  supplier: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  description: string;
  status: 'complet' | 'incomplet' | 'a_verifier';
  linkedFundCalls: Array<{
    fund_call_id: string;
    fund_call_name: string;
    subsidy_name: string;
    allocated_amount: number;
  }>;
}

export function InvoicesTab() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const [filterSubsidy, setFilterSubsidy] = useState('');
  const [filterFundCall, setFilterFundCall] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Filter options
  const [subsidies, setSubsidies] = useState<Array<{ id: string; name: string }>>([]);
  const [fundCalls, setFundCalls] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [invoices, filterSubsidy, filterFundCall, filterSupplier, filterStartDate, filterEndDate]);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load subsidies for filter
      const { data: subsidiesData } = await supabase
        .from('grant_subsidies')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name');

      setSubsidies(subsidiesData || []);

      // Load fund calls for filter
      const { data: fundCallsData } = await supabase
        .from('grant_fund_calls')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name');

      setFundCalls(fundCallsData || []);

      // Load invoices with linked fund calls
      const { data: invoicesData, error } = await supabase
        .from('grant_invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('invoice_date', { ascending: false });

      if (error) throw error;

      // For each invoice, get linked fund calls with subsidy names
      const invoicesWithLinks = await Promise.all(
        (invoicesData || []).map(async (invoice) => {
          const { data: links } = await supabase
            .from('grant_fund_call_invoices')
            .select(`
              fund_call_id,
              allocated_amount,
              fund_call:grant_fund_calls(
                id,
                name,
                subsidy:grant_subsidies(name)
              )
            `)
            .eq('invoice_id', invoice.id);

          const linkedFundCalls = (links || []).map((link: any) => ({
            fund_call_id: link.fund_call.id,
            fund_call_name: link.fund_call.name,
            subsidy_name: link.fund_call.subsidy.name,
            allocated_amount: link.allocated_amount,
          }));

          return {
            ...invoice,
            linkedFundCalls,
          };
        })
      );

      setInvoices(invoicesWithLinks);
      setFilteredInvoices(invoicesWithLinks);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...invoices];

    if (filterSubsidy) {
      filtered = filtered.filter(inv =>
        inv.linkedFundCalls.some(fc => {
          const subsidy = subsidies.find(s => s.name === fc.subsidy_name);
          return subsidy?.id === filterSubsidy;
        })
      );
    }

    if (filterFundCall) {
      filtered = filtered.filter(inv =>
        inv.linkedFundCalls.some(fc => fc.fund_call_id === filterFundCall)
      );
    }

    if (filterSupplier) {
      filtered = filtered.filter(inv =>
        inv.supplier.toLowerCase().includes(filterSupplier.toLowerCase())
      );
    }

    if (filterStartDate) {
      filtered = filtered.filter(inv => inv.invoice_date >= filterStartDate);
    }

    if (filterEndDate) {
      filtered = filtered.filter(inv => inv.invoice_date <= filterEndDate);
    }

    setFilteredInvoices(filtered);
  };

  const clearFilters = () => {
    setFilterSubsidy('');
    setFilterFundCall('');
    setFilterSupplier('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const hasActiveFilters = filterSubsidy || filterFundCall || filterSupplier || filterStartDate || filterEndDate;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      complet: 'bg-green-100 text-green-800',
      incomplet: 'bg-yellow-100 text-yellow-800',
      a_verifier: 'bg-orange-100 text-orange-800',
    };

    const labels = {
      complet: 'Complet',
      incomplet: 'Incomplet',
      a_verifier: 'À vérifier',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status as keyof typeof colors]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Achats / Factures</h2>
          <p className="mt-1 text-sm text-gray-600">
            Vue transversale de toutes les factures et leurs financements
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouvelle facture
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Filtres</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" />
              Effacer les filtres
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subvention</label>
            <select
              value={filterSubsidy}
              onChange={(e) => setFilterSubsidy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            >
              <option value="">Toutes</option>
              {subsidies.map(subsidy => (
                <option key={subsidy.id} value={subsidy.id}>{subsidy.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ADF</label>
            <select
              value={filterFundCall}
              onChange={(e) => setFilterFundCall(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            >
              <option value="">Tous</option>
              {fundCalls.map(fc => (
                <option key={fc.id} value={fc.id}>{fc.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
            <input
              type="text"
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              placeholder="Rechercher..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mt-3 text-sm text-gray-600">
            {filteredInvoices.length} facture(s) trouvée(s)
          </div>
        )}
      </div>

      {/* Invoices list */}
      {filteredInvoices.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {hasActiveFilters ? 'Aucune facture trouvée' : 'Aucune facture'}
          </h3>
          <p className="text-gray-600 mb-4">
            {hasActiveFilters
              ? 'Essayez de modifier vos filtres'
              : 'Créez votre première facture pour commencer la traçabilité'}
          </p>
          {!hasActiveFilters && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Créer une facture
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fournisseur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Facture
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subventions / ADF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{invoice.supplier}</div>
                      {invoice.description && (
                        <div className="text-sm text-gray-600 truncate max-w-xs">{invoice.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invoice.invoice_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-6 py-4">
                      {invoice.linkedFundCalls.length === 0 ? (
                        <span className="text-sm text-gray-400">Non lié</span>
                      ) : (
                        <div className="space-y-1">
                          {invoice.linkedFundCalls.map((fc, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="font-medium text-gray-900">{fc.subsidy_name}</div>
                              <div className="text-gray-600">
                                {fc.fund_call_name} • {formatCurrency(fc.allocated_amount)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        Détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateStandaloneInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}

      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onUpdate={() => {
            loadData();
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
}
